

class PrescriptionDataManager {
  constructor() {
    this.fieldMap = {
      'od-sphere': 'od_sphere',
      'od-cylinder': 'od_cylinder',
      'od-axis': 'od_axis',
      'os-sphere': 'os_sphere',
      'os-cylinder': 'os_cylinder',
      'os-axis': 'os_axis',
      'ipd': 'ipd'
    };
  }

  loadFromStorage() {
    const data = localStorage.getItem('prescription-data-json');
    return data ? JSON.parse(data) : null;
  }

  saveToStorage(data) {
    localStorage.setItem('prescription-data', JSON.stringify(data));
    localStorage.setItem('prescription-data-json', JSON.stringify(data));
  }

  getValue(data, fieldName) {
    if (!data) return 0;
    const key = this.fieldMap[fieldName];
    return key ? data[key] : 0;
  }

  parseFormData(formData) {
    const result = {};
    Object.keys(this.fieldMap).forEach(fieldName => {
      let value = parseFloat(formData[fieldName]) || 0;
      if (fieldName.includes('sphere') || fieldName.includes('cylinder')) {
        value = parseFloat(formData[fieldName]?.replace('+', '')) || 0;
      }
      result[fieldName] = value;
    });
    return result;
  }

  formatForStorage(formData) {
    return {
      od_sphere: formData['od-sphere'],
      od_cylinder: formData['od-cylinder'],
      od_axis: formData['od-axis'],
      os_sphere: formData['os-sphere'],
      os_cylinder: formData['os-cylinder'],
      os_axis: formData['os-axis'],
      ipd: formData['ipd']
    };
  }
}



// ============================================================================
// VALIDATOR (pure)
// ============================================================================
class PrescriptionValidator {
  static validateField(fieldName, value, formData = {}) {
    if (isNaN(value)) return 'error';

    if (fieldName.includes('sphere')) {
      // return Math.abs(value) > 8 ? 'warning' : 'success';
      return Math.abs(value) > 20 ? 'warning' : 'success';
    }

    if (fieldName.includes('cylinder')) {
      if (value === 0) return 'success';
      // return Math.abs(value) > 8 ? 'warning' : 'success';
      return Math.abs(value) > 20 ? 'warning' : 'success';
    }

    if (fieldName.includes('axis')) {
      // Cross-relation handled in the component (DOM). Keep this pure.
      return 'success';
    }

    if (fieldName === 'ipd') {
      if (value > 0 && (value < 51 || value > 77)) return 'warning';
      return 'success';
    }

    return 'success';
  }

  static validateForm(formData) {
    const errors = [];
    const warnings = [];

    const hasSphere = formData['od-sphere'] !== 0 || formData['os-sphere'] !== 0;
    if (!hasSphere) errors.push(theme.strings.prescriptionVaildation.errors.sphereRequired);

    if (formData['od-cylinder'] !== 0 && formData['od-axis'] === 0) {
      errors.push(theme.strings.prescriptionVaildation.errors.rightCylinderRequiresAxis);
    }
    if (formData['os-cylinder'] !== 0 && formData['os-axis'] === 0) {
      errors.push(theme.strings.prescriptionVaildation.errors.leftCylinderRequiresAxis);
    }

    if (formData['od-axis'] !== 0 && formData['od-cylinder'] === 0) {
      errors.push(theme.strings.prescriptionVaildation.errors.rightAxisRequiresCylinder);
    }
    if (formData['os-axis'] !== 0 && formData['os-cylinder'] === 0) {
      errors.push(theme.strings.prescriptionVaildation.errors.leftAxisRequiresCylinder);
    }

    if (formData['ipd'] > 0 && (formData['ipd'] < 51 || formData['ipd'] > 77)) {
      warnings.push(theme.strings.prescriptionVaildation.warnings.ipdOutOfRange);
    }

    return { errors, warnings };
  }

  static validateFieldset(fieldsetType, formData) {
    const errors = [];
    const warnings = [];

    switch (fieldsetType) {
      case 'sphere':
        if (Math.abs(formData['od-sphere']) > 20 || Math.abs(formData['os-sphere']) > 20) {
          warnings.push(theme.strings.prescriptionVaildation.warnings.highSphereValues);
        }
        if (formData['od-sphere'] !== 0 && formData['os-sphere'] !== 0) {
          if ((formData['od-sphere'] > 0 && formData['os-sphere'] < 0) ||
            (formData['od-sphere'] < 0 && formData['os-sphere'] > 0)) {
            warnings.push(theme.strings.prescriptionVaildation.warnings.oppositeSigns);
          }
          const sphereDiff = Math.abs(formData['od-sphere'] - formData['os-sphere']);
          if (sphereDiff > 3) warnings.push(theme.strings.prescriptionVaildation.warnings.differentPrescriptions);
        }
        if (formData['od-sphere'] === 0 && formData['os-sphere'] === 0) {
          warnings.push(theme.strings.prescriptionVaildation.warnings.zeroValues);
        }
        break;

      case 'cylinder':
        if (Math.abs(formData['od-cylinder']) > 20 || Math.abs(formData['os-cylinder']) > 20) {
          warnings.push(theme.strings.prescriptionVaildation.warnings.highCylinderValues);
        }
        if (formData['od-cylinder'] !== 0 && formData['od-axis'] === 0) {
          errors.push(theme.strings.prescriptionVaildation.errors.rightCylinderRequiresAxis);
        }
        if (formData['os-cylinder'] !== 0 && formData['os-axis'] === 0) {
          errors.push(theme.strings.prescriptionVaildation.errors.leftCylinderRequiresAxis);
        }
        break;

      case 'axis':
        if (formData['od-axis'] !== 0 && formData['od-cylinder'] === 0) {
          errors.push(theme.strings.prescriptionVaildation.errors.rightAxisRequiresCylinder);
        }
        if (formData['os-axis'] !== 0 && formData['os-cylinder'] === 0) {
          errors.push(theme.strings.prescriptionVaildation.errors.leftAxisRequiresCylinder);
        }
        if (formData['od-axis'] < 0 || formData['od-axis'] > 180) {
          warnings.push(theme.strings.prescriptionVaildation.warnings.axisOutOfRange);
        }
        if (formData['os-axis'] < 0 || formData['os-axis'] > 180) {
          warnings.push(theme.strings.prescriptionVaildation.warnings.leftAxisOutOfRange);
        }
        break;

      case 'ipd':
        if (formData['ipd'] > 0 && (formData['ipd'] < 51 || formData['ipd'] > 77)) {
          warnings.push(theme.strings.prescriptionVaildation.warnings.ipdOutOfNormalRange);
        }
        if (formData['ipd'] > 0 && formData['ipd'] < 55) warnings.push(theme.strings.prescriptionVaildation.warnings.ipdQuiteSmall);
        if (formData['ipd'] > 70) warnings.push(theme.strings.prescriptionVaildation.warnings.ipdQuiteLarge);
        break;
    }

    return { errors, warnings };
  }
}

// ============================================================================
// PRESCRIPTION FORM COMPONENT
// ============================================================================

class PrescriptionForm extends HTMLElement {
  constructor() {
    super();
    this.dataManager = new PrescriptionDataManager();
  }

  connectedCallback() {
    this.loadData();
    this.bindEvents();

    // Listen for prescription data clearing events to reset form
    window.addEventListener('prescription-data:cleared', () => {
      console.log('PrescriptionForm: prescription data cleared, resetting form');
      this.loadData(); // This will load default data when localStorage is empty
    });

    this.addEventListener('form:reset', () => {
      this.updateSubmitButtonState();
    });
  }

  loadData() {
    const data = this.dataManager.loadFromStorage();
    this.populateFields(data);

    if (data) {
      this.validateLoadedData();
    }
  }

  populateFields(data) {
    this.querySelectorAll('[data-prescription-value]').forEach(field => {
      const fieldName = field.getAttribute('data-prescription-value');
      const value = this.dataManager.getValue(data, fieldName);

      if (value !== undefined && value !== null && value !== 0) {
        if (fieldName.includes('sphere') || fieldName.includes('cylinder')) {
          const formattedValue = value >= 0 ? `+${value}` : value.toString();

          if (field.tagName === 'SELECT') {
            // For select elements, find and select the correct option
            const option = field.querySelector(`option[value="${formattedValue}"]`);
            if (option) {
              option.selected = true;
              field.value = formattedValue;

              // Trigger NativeSelect validation first
              const nativeSelect = field.closest('native-select');
              if (nativeSelect && nativeSelect.validate) {
                nativeSelect.validate();
              }

              field.dispatchEvent(new Event('change', { bubbles: true }));

              // Wait for any custom validation to complete, then run our validation
              setTimeout(() => {
                if (this.validateField) {
                  this.validateField(field);
                }
              }, 0);
            }
          } else {
            field.value = formattedValue;
          }
        } else {
          const stringValue = value.toString();

          if (field.tagName === 'SELECT') {
            // For select elements, find and select the correct option
            const option = field.querySelector(`option[value="${stringValue}"]`);
            if (option) {
              option.selected = true;
              field.value = stringValue;

              // Trigger NativeSelect validation first
              const nativeSelect = field.closest('native-select');
              if (nativeSelect && nativeSelect.validate) {
                nativeSelect.validate();
              }

              field.dispatchEvent(new Event('change', { bubbles: true }));

              // Wait for any custom validation to complete, then run our validation
              setTimeout(() => {
                if (this.validateField) {
                  this.validateField(field);
                }
              }, 0);
            }
          } else {
            field.value = stringValue;
          }
        }
      }
    });
  }

  validateLoadedData() {
    const fields = this.querySelectorAll('[data-prescription-value]');
    fields.forEach(field => this.validateField(field));
    this.updateSubmitButtonState();
  }

  validateRelatedFields(changedFieldName) {
    // Define field dependencies for cross-validation
    const dependencies = {
      'od-cylinder': ['od-axis'],
      'os-cylinder': ['os-axis'],
      'od-axis': ['od-cylinder'],
      'os-axis': ['os-cylinder']
    };

    const relatedFields = dependencies[changedFieldName] || [];
    relatedFields.forEach(fieldName => {
      const field = this.querySelector(`[data-prescription-value="${fieldName}"]`);
      if (field) {
        this.validateField(field, true); // Skip related validation to prevent recursion
      }
    });
  }

  updateSubmitButtonState() {
    const submitButton =
      this.querySelector('[data-prescription-submit]') ||
      document.querySelector(`save-prescription-button[data-target-component-id="prescription-form-${this.id}"] button`) ||
      this.closest('sub-drawer-dialog')?.querySelector('save-prescription-button button');

    if (!submitButton) return;

    const formData = this.getFormData();
    const { errors /*, warnings */ } = PrescriptionValidator.validateForm(formData);

    // A field is “ok” if it’s success OR it’s neutral ("" or 0 for cyl/axis).
    const fields = this.querySelectorAll('[data-prescription-value]');
    const allValid = Array.from(fields).every(f => {
      const name = f.getAttribute('data-prescription-value');
      const v = this.parseValue(f.value, name);
      const neutral = (name.includes('cylinder') || name.includes('axis')) ? (v === 0 || Number.isNaN(v)) : false;
      return neutral || f.classList.contains('input-success');
    });

    // Submit should be blocked only by ERRORS
    const canSubmit = allValid && errors.length === 0;

    submitButton.disabled = !canSubmit;
    submitButton.classList.toggle('btn--disabled', !canSubmit);
  }



  bindEvents() {
    console.log('PrescriptionForm: binding events');
    const fields = this.querySelectorAll('[data-prescription-value]');
    console.log('PrescriptionForm: found fields:', fields.length);

    fields.forEach(field => {
      field.addEventListener('input', () => this.validateField(field));
      field.addEventListener('change', () => this.validateField(field));
    });

    // Initial validation and submit button state
    this.updateSubmitButtonState();
  }

  validateField(field, skipRelatedValidation = false) {
    const fieldName = field.getAttribute('data-prescription-value');
    const value = this.parseValue(field.value, fieldName);
    const formData = this.getFormData();

    // --- Respect cross-errors before clearing classes ---
    if (fieldName.includes('axis')) {
      const cylField = fieldName.replace('axis', 'cylinder');
      const cyl = formData[cylField] || 0;
      if (cyl !== 0 && value === 0) {
        const visual = field.closest('native-select') || field;
        visual.classList.remove('input-success', 'input-warning', 'input-error');
        visual.classList.add('input-error');
        this.updateSubmitButtonState();
        if (!skipRelatedValidation) this.validateRelatedFields(fieldName);
        return;
      }
    }

    // normal flow
    field.classList.remove('input-success', 'input-warning', 'input-error');
    if (field.value === '' || field.value === undefined) return;

    const validationState = PrescriptionValidator.validateField(fieldName, value, formData);
    field.classList.add(`input-${validationState}`);

    this.validateFieldset(field);
    if (!skipRelatedValidation) this.validateRelatedFields(fieldName);

    // Ensure cross-dependencies are properly highlighted
    this.validateCrossDependencies();

    this.updateSubmitButtonState();
  }


  validateFieldset(changedField) {
    const currentFs = changedField.closest('fieldset');
    if (!currentFs) return;

    const fieldsetType = currentFs.dataset.fieldsetType;
    if (!fieldsetType) return;

    const formData = this.getFormData();
    const { errors, warnings } = PrescriptionValidator.validateFieldset(fieldsetType, formData);

    // 1) Clear stale error classes on all related fields first
    const clear = (name) => {
      const el = this.querySelector(`[data-prescription-value="${name}"]`);
      if (el) el.classList.remove('input-error');
    };
    if (fieldsetType === 'axis') {
      clear('od-axis'); clear('os-axis');
    }
    if (fieldsetType === 'cylinder') {
      clear('od-cylinder'); clear('os-cylinder');
    }

    // 2) Decide which fieldset the red box should be attached to,
    //    based on what is actually missing
    //    (default to the current one if nothing special)
    let anchorFieldset = currentFs;

    // If cylinder is present but axis missing -> errors belong to AXIS
    const odNeedsAxis = formData['od-cylinder'] !== 0 && formData['od-axis'] === 0;
    const osNeedsAxis = formData['os-cylinder'] !== 0 && formData['os-axis'] === 0;
    if (odNeedsAxis || osNeedsAxis) {
      anchorFieldset = this.querySelector('fieldset[data-fieldset-type="axis"]') || currentFs;

      // paint the missing axis fields red
      if (odNeedsAxis) {
        const el = this.querySelector('[data-prescription-value="od-axis"]');
        el && el.classList.add('input-error');
      }
      if (osNeedsAxis) {
        const el = this.querySelector('[data-prescription-value="os-axis"]');
        el && el.classList.add('input-error');
      }
    }

    // If axis is present but cylinder missing -> errors belong to CYLINDER
    const odNeedsCylinder = formData['od-axis'] !== 0 && formData['od-cylinder'] === 0;
    const osNeedsCylinder = formData['os-axis'] !== 0 && formData['os-cylinder'] === 0;
    if (odNeedsCylinder || osNeedsCylinder) {
      anchorFieldset = this.querySelector('fieldset[data-fieldset-type="cylinder"]') || anchorFieldset;

      // paint the missing cylinder fields red
      if (odNeedsCylinder) {
        const el = this.querySelector('[data-prescription-value="od-cylinder"]');
        el && el.classList.add('input-error');
      }
      if (osNeedsCylinder) {
        const el = this.querySelector('[data-prescription-value="os-cylinder"]');
        el && el.classList.add('input-error');
      }
    }

    // 3) Render the existing red box (relocated to the anchor fieldset)
    this.displayFieldsetValidationMessages(anchorFieldset, errors, warnings);

    // 4) Also validate the related fieldset to ensure cross-dependencies are highlighted
    if (fieldsetType === 'axis' && (odNeedsCylinder || osNeedsCylinder)) {
      // If we're validating axis and cylinder is missing, also validate cylinder fieldset
      const cylinderFieldset = this.querySelector('fieldset[data-fieldset-type="cylinder"]');
      if (cylinderFieldset) {
        const cylinderErrors = [];
        if (odNeedsCylinder) cylinderErrors.push(theme.strings.prescriptionVaildation.errors.rightAxisRequiresCylinder);
        if (osNeedsCylinder) cylinderErrors.push(theme.strings.prescriptionVaildation.errors.leftAxisRequiresCylinder);
        this.displayFieldsetValidationMessages(cylinderFieldset, cylinderErrors, []);
      }
    } else if (fieldsetType === 'cylinder' && (odNeedsAxis || osNeedsAxis)) {
      // If we're validating cylinder and axis is missing, also validate axis fieldset
      const axisFieldset = this.querySelector('fieldset[data-fieldset-type="axis"]');
      if (axisFieldset) {
        const axisErrors = [];
        if (odNeedsAxis) axisErrors.push(theme.strings.prescriptionVaildation.errors.rightCylinderRequiresAxis);
        if (osNeedsAxis) axisErrors.push(theme.strings.prescriptionVaildation.errors.leftCylinderRequiresAxis);
        this.displayFieldsetValidationMessages(axisFieldset, axisErrors, []);
      }
    }
  }

  validateCrossDependencies() {
    const formData = this.getFormData();

    // Check if axis values are present but cylinder values are missing
    const odNeedsCylinder = formData['od-axis'] !== 0 && formData['od-cylinder'] === 0;
    const osNeedsCylinder = formData['os-axis'] !== 0 && formData['os-cylinder'] === 0;

    if (odNeedsCylinder || osNeedsCylinder) {
      // Highlight both cylinder fields in red
      if (odNeedsCylinder) {
        const el = this.querySelector('[data-prescription-value="od-cylinder"]');
        if (el) {
          el.classList.remove('input-success', 'input-warning');
          el.classList.add('input-error');
        }
      }
      if (osNeedsCylinder) {
        const el = this.querySelector('[data-prescription-value="os-cylinder"]');
        if (el) {
          el.classList.remove('input-success', 'input-warning');
          el.classList.add('input-error');
        }
      }
    }

    // Check if cylinder values are present but axis values are missing
    const odNeedsAxis = formData['od-cylinder'] !== 0 && formData['od-axis'] === 0;
    const osNeedsAxis = formData['os-cylinder'] !== 0 && formData['os-axis'] === 0;

    if (odNeedsAxis || osNeedsAxis) {
      // Highlight both axis fields in red
      if (odNeedsAxis) {
        const el = this.querySelector('[data-prescription-value="od-axis"]');
        if (el) {
          el.classList.remove('input-success', 'input-warning');
          el.classList.add('input-error');
        }
      }
      if (osNeedsAxis) {
        const el = this.querySelector('[data-prescription-value="os-axis"]');
        if (el) {
          el.classList.remove('input-success', 'input-warning');
          el.classList.add('input-error');
        }
      }
    }
  }

  displayFieldsetValidationMessages(fieldset, errors, warnings) {
    // reuse (or create once) the SAME container inside the chosen fieldset
    let messageContainer = fieldset.querySelector('.validation-messages');

    // If a box exists in some other fieldset, move it here
    if (!messageContainer) {
      const anywhere = this.querySelector('.validation-messages');
      if (anywhere && anywhere.parentElement !== fieldset) {
        messageContainer = anywhere;
        fieldset.appendChild(messageContainer);
      }
    }

    // If still none, create ONE (we’re not adding “new kinds” of UI; this is your existing box)
    if (!messageContainer) {
      messageContainer = document.createElement('div');
      messageContainer.className = 'validation-messages';
      messageContainer.style.cssText = 'margin-top: 0.5rem;';
      fieldset.appendChild(messageContainer);
    }

    // Update content of the single existing box
    messageContainer.innerHTML = '';

    if (errors.length > 0) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'validation-message validation-message--error';
      errorDiv.innerHTML = `
        <strong class="h5 list-title block">${theme.strings.prescriptionVaildation.criticalIssuesTitle}</strong>
        <ul class="list list-unstyled">
          ${errors.map(e => `<li>❌ ${e}</li>`).join('')}
        </ul>
      `;
      messageContainer.appendChild(errorDiv);
    }

    if (warnings.length > 0) {
      const warningDiv = document.createElement('div');
      warningDiv.className = 'validation-message validation-message--warning';
      warningDiv.innerHTML = `
        <strong class="h5 list-title block">${theme.strings.prescriptionVaildation.doubleCheckTitle}</strong>
        <ul class="list list-unstyled">
          ${warnings.map(w => `<li>⚠️ ${w}</li>`).join('')}
        </ul>
      `;
      messageContainer.appendChild(warningDiv);
    }

    // Hide the box if nothing to show
    messageContainer.hidden = errors.length === 0 && warnings.length === 0;
  }


  parseValue(value, fieldName) {
    if (fieldName.includes('sphere') || fieldName.includes('cylinder')) {
      return parseFloat(value.replace('+', ''));
    }
    return parseFloat(value);
  }



  getFormData() {
    const formData = {};
    this.querySelectorAll('[data-prescription-value]').forEach(field => {
      const fieldName = field.getAttribute('data-prescription-value');
      formData[fieldName] = field.value;
    });
    return this.dataManager.parseFormData(formData);
  }

  submit() {
    const formData = this.getFormData();
    const validation = PrescriptionValidator.validateForm(formData);

    if (validation.errors.length > 0) {
      this.showValidationMessages(validation);
      return null;
    }

    const storageData = this.dataManager.formatForStorage(formData);
    this.dataManager.saveToStorage(storageData);

    // Update the hidden prescription input in the main form
    this.updateMainFormPrescription(storageData);

    this.dispatchEvent(new CustomEvent('prescription:submitted', {
      detail: storageData
    }));

    // Fire prescription-data:updated event when valid data is saved
    window.dispatchEvent(new CustomEvent('prescription-data:updated', {
      detail: storageData
    }));

    return storageData;
  }

  // updateMainFormPrescription(prescriptionData) {
  //   // Find the hidden prescription input in the main form
  //   const prescriptionInput = document.querySelector('input[name="properties[Prescription]"]');

  //   if (prescriptionInput) {
  //     // Format prescription data as human-readable text
  //     const humanReadable = this.formatPrescriptionForDisplay(prescriptionData);
  //     prescriptionInput.value = humanReadable;
  //     // console.log('Updated prescription input with:', humanReadable);
  //   } else {
  //     console.warn('Prescription input not found in main form');
  //   }
  // }
    updateMainFormPrescription(prescriptionData) {
    // Find the hidden prescription input in the main form
    const prescriptionInput = document.querySelector('input[name="properties[Prescription]"]');
    const prescriptionEngInput = document.querySelector('input[name="properties[_prescription_eng]"]');

    if (prescriptionInput) {
      // Format prescription data as human-readable text
      const humanReadable = this.formatPrescriptionForDisplay(prescriptionData);
      prescriptionInput.value = humanReadable;
      // console.log('Updated prescription input with:', humanReadable);
    } else {
      console.warn('Prescription input not found in main form');
    }

    if (prescriptionEngInput) {
      // Format prescription data in structured English format
      const structuredEng = this.formatPrescriptionForEng(prescriptionData);
      prescriptionEngInput.value = structuredEng;
      // console.log('Updated prescription_eng input with:', structuredEng);
    } else {
      console.warn('Prescription_eng input not found in main form');
    }
  }

  formatPrescriptionForDisplay(data) {
    const formatEye = (eye, sphere, cylinder, axis) => {
      const sphereText = sphere >= 0 ? `+${sphere}` : sphere.toString();
      const cylinderText = cylinder >= 0 ? `+${cylinder}` : cylinder.toString();
      return `${eye}: ${sphereText} SPH, ${cylinderText} CYL @ ${axis}°`;
    };

    const rightEye = formatEye(theme.strings.prescriptionVaildation.table.rightEyeOd, data.od_sphere, data.od_cylinder, data.od_axis);
    const leftEye = formatEye(theme.strings.prescriptionVaildation.table.leftEyeOs, data.os_sphere, data.os_cylinder, data.os_axis);
    const ipdText = `${theme.strings.prescriptionVaildation.table.ipdLabel} ${data.ipd}${theme.strings.prescriptionVaildation.table.ipdUnit}`;

    return `${rightEye} | ${leftEye} | ${ipdText}`;
  }

  formatPrescriptionForEng(data) {
    const formatEyeEng = (eye, sphere, cylinder, axis) => {
      const sphereText = sphere >= 0 ? `+${sphere}` : sphere.toString();
      const cylinderText = cylinder >= 0 ? `+${cylinder}` : cylinder.toString();
      return `${eye}: Sphere ${sphereText}, Cylinder ${cylinderText}, Axis ${axis} degrees`;
    };

    const rightEye = formatEyeEng('Right Eye', data.od_sphere, data.od_cylinder, data.od_axis);
    const leftEye = formatEyeEng('Left Eye', data.os_sphere, data.os_cylinder, data.os_axis);
    const ipdText = `Interpupillary Distance: ${data.ipd} millimeters`;

    return `${rightEye} | ${leftEye} | ${ipdText}`;
  }

  showValidationMessages(validation) {
    // Simple validation display - can be enhanced
    if (validation.errors.length > 0) {
      console.error('Validation errors:', validation.errors);
    }
    if (validation.warnings.length > 0) {
      console.warn('Validation warnings:', validation.warnings);
    }
  }
}

customElements.define('prescription-form', PrescriptionForm);

// ============================================================================
// SAVE PRESCRIPTION BUTTON
// ============================================================================

class SavePrescriptionButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button.addEventListener('click', () => this.save());
  }

  save() {
    const targetId = this.getAttribute('data-target-component-id');

    if (!targetId) {
      console.error('Missing data-target-component-id');
      return;
    }

    const form = document.querySelector(`prescription-form#${targetId}`);

    if (!form) {
      console.error(`Prescription form with ID "${targetId}" not found`);
      return;
    }

    const data = form.submit();

    if (data) {
      const subdrawer = this.closest('sub-drawer-dialog');
      if (subdrawer) subdrawer.close();

      this.dispatchEvent(new CustomEvent('prescription:saved', {
        detail: { prescriptionData: data, componentId: targetId }
      }));
    }
  }
}

customElements.define('save-prescription-button', SavePrescriptionButton);

// ============================================================================
// LENS SELECTION DRAWER
// ============================================================================

class LensSelectionDrawer extends SideDrawer {
  connectedCallback() {
    this.prescriptionTable = null;
    this.previewImageLoaded = false;
    this.productForm = this.querySelector('product-form');
    this.gallery = this.querySelector('[data-lens-drawer-gallery]');
    this.variantPicker = this.querySelector('variant-picker');
    this.tabs = this.querySelector('site-tabs');
    this.isMobile = window.matchMedia('(max-width: 768px)').matches;
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', this.handleOpenClick.bind(this));
    // document.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
    this.tabs.addEventListener('on:tab:change', this.handleTabChange.bind(this));
    this.variantPicker.addEventListener('on:variant:change', this.generateSummary.bind(this));
    this.productForm.addEventListener('on:cart:add', () => this.close());
    window.addEventListener('prescription-data:updated', () => {
      if (this.prescriptionTable) {
        this.prescriptionTable.render();
      }
      // Navigate to the last available tab (step-3) if it exists
      if (this.tabs && this.tabs.tabTriggers && this.tabs.tabTriggers.length >= 3) {
        this.tabs.setActiveTab(this.tabs.tabTriggers[2]); // Index 2 = step-3
      }

      NotificationCenter.show(theme.strings.prescriptionVaildation.prescriptionUpdated, { container: ".lens-selection-drawer__content", type: "success" });
    });
    window.addEventListener('prescription-data:cleared', () => {
      if (this.prescriptionTable) {
        this.prescriptionTable.render();
      }
    });
  }

  handleTabChange(evt) {
    if (evt.detail.isLastTab) {
      this.switchButtons(1);
    } else {
      this.switchButtons(0);
    }
  }

  switchButtons(index) {
    const buttons = this.querySelector('layered-button-stack');
    buttons.showButton(index);

    // Force re-render and set attribute directly for reliability
    setTimeout(() => {
      buttons.dispatchEvent(new Event('resize'));
      buttons.setAttribute('active-index', String(index));
    }, 50);
  }

  handleOpenClick(evt) {
    if (evt.target.matches('#open-lens-selection-drawer')) {
      evt.preventDefault();
      this.open(evt.target);
    }
  }

  async generateSummary() {
    const summaryContainer = this.querySelector('section-renderer[data-lens-drawer-summary]');
    if (!summaryContainer) return;

    // Update the variant ID attribute if needed
    const variantId = this.variantPicker?.getSelectedVariant?.().id;
    if (variantId) {
      summaryContainer.setAttribute('data-section-variant-id', variantId);
    }

    // console.log('this.variantPicker', this.variantPicker);
    // console.log('this.variantPicker.getSelectedVariant()', this.variantPicker.getSelectedVariant());

    // Use the existing SectionRenderer to handle the rendering
    await summaryContainer.render();
    this.prescriptionTable = summaryContainer.querySelector('prescription-table');
  }

  open(opener, elementToFocus, callback) {
    super.open(opener, elementToFocus, callback);

    // Trigger variant picker to update its options
    if (this.variantPicker) {
      // Trigger a change event on the first option selector to update the picker
      const firstOptionSelector = this.variantPicker.querySelector('.option-selector input:checked, .option-selector .custom-select__option[aria-selected="true"]');
      if (firstOptionSelector) {
        firstOptionSelector.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (!this.isMobile) {
      this.renderFeaturedMedia();
      this.toggleGalleryVisibility(true);
    }
  }

  close(callback) {
    this.toggleGalleryVisibility(false, 100);
    setTimeout(() => {
      super.close(callback);
      this.previewImageLoaded = false;
      this.currentMediaSrc = null;
    }, 400);

    setTimeout(() => {
      this.tabs.setActiveTab(this.tabs.tabTriggers[0]);
    }, 1000);
  }

  getVariantFeaturedMedia() {
    const variant = this.variantPicker?.getSelectedVariant?.();
    // console.log('variant', variant);
    return variant?.featured_image || null;
  }

  renderFeaturedMedia() {
    const featuredMedia = this.getVariantFeaturedMedia();
    if (!featuredMedia || !featuredMedia.src) return;

    // Only update if image changed
    if (this.currentMediaSrc === featuredMedia.src) return;

    const img = document.createElement('img');
    img.src = featuredMedia.src;
    img.alt = featuredMedia.alt || '';

    const preview = this.querySelector('[data-lens-preview-image]');
    preview.innerHTML = '';
    preview.appendChild(img);

    this.previewImageLoaded = true;
    this.currentMediaSrc = featuredMedia.src;
  }

  toggleGalleryVisibility(visible, delay = 400) {
    setTimeout(() => {
      this.gallery?.setAttribute('aria-hidden', !visible);
    }, delay);
  }
}

customElements.define('lens-selection-drawer', LensSelectionDrawer);


class PrescriptionTable extends HTMLElement {
  connectedCallback() {
    this.render();

    // Listen for prescription data changes and clearing events
    window.addEventListener('prescription-data:updated', () => this.update());
    window.addEventListener('prescription-data:cleared', () => this.update());
  }

  get prescriptionData() {
    const prescriptionData = localStorage.getItem('prescription-data');
    return prescriptionData ? JSON.parse(prescriptionData) : null;
  }

  render() {
    const data = this.prescriptionData;
    if (!data) {
      this.innerHTML = `
        <span class="validation-message validation-message--clear w-full validation-message--justify">
          <span class="body-text">${theme.strings.prescriptionVaildation.sendItLater}</span>
          <sub-drawer-dialog-opener data-id="#prescription-input-helper-subdrawer">
            <button type="button" class="btn btn--primary btn--small">${theme.strings.prescriptionVaildation.enterNow}</button>
          </sub-drawer-dialog-opener>
        </span>
      `;
      return;
    }

    this.innerHTML = `
      <div class="prescription-table">
        <table class="prescription-table__table rounded-table">
          <thead>
            <tr>
              <th>${theme.strings.prescriptionVaildation.table.eye}</th>
              <th>${theme.strings.prescriptionVaildation.table.sphere}</th>
              <th>${theme.strings.prescriptionVaildation.table.cylinder}</th>
              <th>${theme.strings.prescriptionVaildation.table.axis}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${theme.strings.prescriptionVaildation.table.rightEyeOd}</td>
              <td>${this.formatValue(data.od_sphere)}</td>
              <td>${this.formatValue(data.od_cylinder)}</td>
              <td>${this.formatValue(data.od_axis)}</td>
            </tr>
            <tr>
              <td>${theme.strings.prescriptionVaildation.table.leftEyeOs}</td>
              <td>${this.formatValue(data.os_sphere)}</td>
              <td>${this.formatValue(data.os_cylinder)}</td>
              <td>${this.formatValue(data.os_axis)}</td>
            </tr>
          </tbody>
        </table>
        <div class="prescription-table__ipd">
          <strong>${theme.strings.prescriptionVaildation.table.ipdLabel}</strong> ${this.formatValue(data.ipd)} ${theme.strings.prescriptionVaildation.table.ipdUnit}
        </div>
      </div>
    `;
  }

  formatValue(value) {
    if (value === null || value === undefined || value === 0) {
      return '-';
    }
    return value.toString();
  }

  update() {
    this.render();
  }
}

customElements.define('prescription-table', PrescriptionTable);


class PrescriptionRadioHandler extends HTMLElement {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('change', this.handleRadioChange.bind(this));
    window.addEventListener('prescription-data:updated', () => this.uncheckRadioButtons());
  }

  handleRadioChange(event) {
    if (event.target.name === 'prescription-type' && event.target.type === 'radio') {
      if (event.target.hasAttribute('data-clear-prescription')) {
        this.clearPrescriptionData();
      }
    }
  }

  uncheckRadioButtons() {
    const radioButtons = document.querySelectorAll('input[name="prescription-type"]');
    radioButtons.forEach(radio => radio.checked = false);
    const enterPrescriptionRadio = document.querySelector('input[name="prescription-type"][data-enter-prescription-radio]');
    if (enterPrescriptionRadio && !enterPrescriptionRadio.checked) {
      enterPrescriptionRadio.checked = true;
    }
  }

  // TODO: FIX LATER
  clearPrescriptionData() {
    try {

      localStorage.removeItem('prescription-data');

      const targetInput = document.querySelector('input[data-target="lens-form-user-prescription"]');

      if (targetInput) {
        targetInput.value = theme.strings.prescriptionVaildation.sendItLater;
        // console.log('Prescription target input cleared and set to "Send it later"');
      } else {
        console.warn('Target prescription input not found');
      }

      window.dispatchEvent(new CustomEvent('prescription-data:cleared', {
        detail: { reason: 'send-it-later-selected' }
      }));
    } catch (error) {
      console.warn('Failed to clear prescription data:', error);
    }
  }
}

customElements.define('prescription-radio-handler', PrescriptionRadioHandler);